import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useSectionTheme } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, ExternalLink, Wand2, Pencil, Users, Copy, RefreshCw, Loader2, Star, UserPlus, Activity, Briefcase as BriefcaseIcon, Sparkles } from "lucide-react";
import DataTable, { SelectInlineEditor } from "@/components/DataTable";
import JobProgress from "@/components/JobProgress";
import JobQualityBadge from "@/components/JobQualityBadge";
import SkillHealthBadge from "@/components/SkillHealthBadge";
import BulkAssignRecruitersDialog from "@/components/BulkAssignRecruitersDialog";
import BulkJobEditDialog from "@/components/BulkJobEditDialog";
import BulkInterviewAssignDialog from "@/components/BulkInterviewAssignDialog";
import BulkPushSkillsDialog from "@/components/BulkPushSkillsDialog";
import { SpotlightPickerTrigger } from "@/components/SpotlightPicker";

const STATUS_TABS = [
    { key: "", label: "All" },
    { key: "active", label: "Active" },
    { key: "draft", label: "Draft" },
    { key: "paused", label: "Paused" },
    { key: "closed", label: "Closed" },
];

const STATUS_STYLE = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    draft: "bg-slate-100 text-slate-700 border-slate-300",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    closed: "bg-rose-50 text-rose-700 border-rose-200",
};

const PRIORITY_STYLE = {
    urgent: "text-rose-700 border-rose-300 bg-rose-50",
    high: "text-amber-700 border-amber-300 bg-amber-50",
    normal: "text-slate-600 border-slate-300 bg-white",
    low: "text-slate-400 border-slate-200 bg-white",
};

// Numeric ranking so the Priority column sorts as urgent > high > normal > low
// (a plain string sort would order alphabetically — h,l,n,u — which is wrong).
const PRIORITY_RANK = { urgent: 4, high: 3, normal: 2, low: 1 };

const STATUS_OPTIONS = [["active", "Active"], ["draft", "Draft"], ["paused", "Paused"], ["closed", "Closed"]];
const URGENCY_OPTIONS = [["Immediate", "Immediate"], ["15 days", "15 days"], ["30 days", "30 days"], ["Flexible", "Flexible"]];
const PRIORITY_OPTIONS = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];

function fmtDate(iso) {
    if (!iso) return null;
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Phase 55.6 — session-cache for jobs + quality so navigating back to /jobs
// from a job detail/edit page renders instantly instead of triggering the
// heavy `/jobs` + `/jobs/quality/bulk` re-fetch. Cache TTL is short (30s)
// so a recruiter who genuinely wants fresh data hits the Refresh button.
const CACHE_KEY = "jobsListCache:v1";
const CACHE_TTL_MS = 30_000;

function readJobsCache() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (Date.now() - (o.ts || 0) > CACHE_TTL_MS) return null;
        return o;
    } catch { return null; }
}

function writeJobsCache(jobs, quality) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ jobs, quality, ts: Date.now() })); }
    catch { /* quota / private mode → silently skip */ }
}

function healthCls(score) {
    if (score >= 70) return "border-emerald-300 bg-emerald-50 text-emerald-700";
    if (score >= 40) return "border-amber-300 bg-amber-50 text-amber-700";
    return "border-rose-300 bg-rose-50 text-rose-700";
}

const SLA_CLS = {
    ok: "border-emerald-300 bg-emerald-50 text-emerald-700",
    warning: "border-amber-300 bg-amber-50 text-amber-700",
    breach: "border-rose-300 bg-rose-50 text-rose-700 font-bold",
};

export default function JobsList() {
    useSectionTheme("jobs_list");
    // Prime initial state from session cache so the table renders the same
    // millisecond the route mounts — no flash of empty list while waiting.
    const initial = readJobsCache();
    const [jobs, setJobs] = useState(initial?.jobs || []);
    const [quality, setQuality] = useState(initial?.quality || {});
    const [status, setStatus] = useState("active");
    // R21 — JD Health filter. Surfaces stale postings that reference obsolete
    // skills — pairs with the trend-shift digest so recruiters can triage.
    const [healthFilter, setHealthFilter] = useState("");   // "" | "healthy" | "risky" | "obsolete"
    // B7 — richer filter set for the Jobs list. Each dropdown is client-side
    // (we already have every job in memory) so filter changes are instant.
    const [priorityFilter, setPriorityFilter] = useState("");
    const [workModeFilter, setWorkModeFilter] = useState("");
    const [urgencyFilter, setUrgencyFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [industryFilter, setIndustryFilter] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [loading, setLoading] = useState(!initial);
    const [insights, setInsights] = useState({});      // job_id -> smart pipeline metrics
    const [recruiters, setRecruiters] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [assignOpen, setAssignOpen] = useState(false);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [bulkInterviewOpen, setBulkInterviewOpen] = useState(false);
    const [bulkSkillsOpen, setBulkSkillsOpen] = useState(false);
    const [bulkCandidateIds, setBulkCandidateIds] = useState([]);
    const [bulkCandLoading, setBulkCandLoading] = useState(false);
    const navigate = useNavigate();

    // Health/SLA/team metrics from the smart-pipeline engine (light payload, no apps)
    const loadInsights = async () => {
        try {
            const { data } = await api.get("/pipeline/smart", { params: { include_apps: 0 } });
            const map = {};
            (data.jobs || []).forEach((j) => { map[j.id] = j; });
            setInsights(map);
            setRecruiters(data.recruiters || []);
        } catch { /* silent — columns show — */ }
    };
    useEffect(() => { loadInsights(); }, []);

    // Fetch candidate IDs for selected jobs → used by the Interview/Skills bulk actions.
    // Dedupes across jobs so the same candidate (applied to multiple jobs) is counted once.
    const openBulkForSelectedJobs = async (which) => {
        if (selected.size === 0) return;
        setBulkCandLoading(true);
        try {
            const jobIds = [...selected];
            const results = await Promise.all(
                jobIds.map((jid) => api.get(`/jobs/${jid}/applications`).then((r) => r.data || []).catch(() => []))
            );
            const ids = new Set();
            results.forEach((apps) => (apps || []).forEach((a) => { if (a?.candidate_id) ids.add(a.candidate_id); }));
            const list = [...ids];
            if (list.length === 0) {
                toast.error("No candidates found on the selected jobs");
                return;
            }
            setBulkCandidateIds(list);
            if (which === "interview") setBulkInterviewOpen(true);
            else setBulkSkillsOpen(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBulkCandLoading(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/jobs");
            const list = data || [];
            setJobs(list);
            // Fetch quality scores for the badge column (best-effort, non-blocking)
            let q = {};
            try {
                const r = await api.get("/jobs/quality/bulk");
                q = r.data || {};
                setQuality(q);
            } catch { /* silent — column will just show — */ }
            writeJobsCache(list, q);
        } finally { setLoading(false); }
    };
    // Mount: skip the network round-trip when we have a fresh session cache.
    useEffect(() => { if (!initial) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const counts = useMemo(() => {
        const c = { "": jobs.length };
        for (const j of jobs) c[j.status] = (c[j.status] || 0) + 1;
        return c;
    }, [jobs]);

    const filteredJobs = useMemo(() => {
        let arr = jobs;
        if (status) arr = arr.filter((j) => j.status === status);
        if (priorityFilter) arr = arr.filter((j) => (j.priority || "normal").toLowerCase() === priorityFilter);
        if (workModeFilter) arr = arr.filter((j) => (j.work_mode || "").toLowerCase() === workModeFilter);
        if (urgencyFilter) arr = arr.filter((j) => (j.urgency || "").toLowerCase() === urgencyFilter);
        if (departmentFilter) arr = arr.filter((j) => (j.department || "").toLowerCase() === departmentFilter.toLowerCase());
        if (industryFilter) arr = arr.filter((j) => (j.industry || "").toLowerCase() === industryFilter.toLowerCase());
        if (searchQ) {
            const q = searchQ.trim().toLowerCase();
            arr = arr.filter((j) =>
                (j.title || "").toLowerCase().includes(q) ||
                (j.slug || "").toLowerCase().includes(q) ||
                (j.department || "").toLowerCase().includes(q) ||
                (j.industry || "").toLowerCase().includes(q) ||
                (j.required_skills || []).some((s) => (s || "").toLowerCase().includes(q)),
            );
        }
        if (healthFilter === "risky") {
            arr = arr.filter((j) => {
                const h = j.skill_health;
                if (!h) return false;
                return (h.score ?? 100) < 55
                    || (h.breakdown?.declining || []).length > 0
                    || (h.breakdown?.obsolete || []).length > 0;
            });
        } else if (healthFilter === "obsolete") {
            arr = arr.filter((j) => (j.skill_health?.breakdown?.obsolete || []).length > 0);
        } else if (healthFilter === "healthy") {
            arr = arr.filter((j) => (j.skill_health?.score ?? 0) >= 75);
        }
        return arr;
    }, [jobs, status, priorityFilter, workModeFilter, urgencyFilter, departmentFilter, industryFilter, searchQ, healthFilter]);

    // Derive distinct dropdown options from the current job list so we only
    // show filters that actually match something.
    const filterOptions = useMemo(() => {
        const dept = new Set();
        const industry = new Set();
        for (const j of jobs) {
            if (j.department) dept.add(j.department);
            if (j.industry) industry.add(j.industry);
        }
        return {
            departments: [...dept].sort(),
            industries: [...industry].sort(),
        };
    }, [jobs]);

    const updateJob = async (job, patch, optimistic = true) => {
        const prev = jobs;
        if (optimistic) {
            setJobs((arr) => arr.map((j) => (j.id === job.id ? { ...j, ...patch } : j)));
        }
        try {
            const { data } = await api.patch(`/jobs/${job.id}`, patch);
            // Patch the row AND keep the session cache in sync so back-nav
            // doesn't surface stale data.
            setJobs((arr) => {
                const next = arr.map((j) => (j.id === job.id ? { ...j, ...data } : j));
                writeJobsCache(next, quality);
                return next;
            });
            toast.success("Saved");
        } catch (err) {
            toast.error(formatApiError(err));
            setJobs(prev);
        }
    };

    const onCellEdit = async ({ row, column, value }) => {
        const numeric = ["salary_min", "salary_max", "experience_min", "experience_max", "openings"];
        let v = value;
        if (numeric.includes(column.key)) {
            v = (v === "" || v === null) ? null : Number(v);
            if (v !== null && Number.isNaN(v)) { toast.error("Must be a number"); return; }
        }
        await updateJob(row, { [column.key]: v });
    };

    const toggleSelect = (id) => setSelected((cur) => {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const columns = useMemo(() => [
        {
            key: "select", label: "", width: 36, sortable: false, filterable: false,
            render: (j) => (
                <input
                    type="checkbox"
                    checked={selected.has(j.id)}
                    onChange={() => toggleSelect(j.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                    data-testid={`job-select-${j.id}`}
                />
            ),
        },
        {
            key: "title", label: "Title", width: 260, sortable: true, editable: true,
            render: (j) => (
                <div className="min-w-0">
                    <div className="font-medium truncate text-foreground">{j.title}</div>
                    <div className="text-[11px] font-mono text-foreground/60 mt-0.5 truncate">
                        {[j.department, j.industry].filter(Boolean).join(" · ") || j.slug}
                    </div>
                </div>
            ),
        },
        {
            key: "progress", label: "Progress", width: 200, sortable: true, filterable: false,
            accessor: (j) => (j.hires_count || 0) / Math.max(1, j.openings || 1),
            render: (j) => <JobProgress job={j} />,
        },
        {
            key: "status", label: "Status", width: 100, sortable: true, editable: true,
            editor: (props) => <SelectInlineEditor {...props} options={STATUS_OPTIONS} />,
            render: (j) => (
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${STATUS_STYLE[j.status] || "bg-secondary border-border"}`} data-testid={`job-status-${j.id}`}>
                    {j.status}
                </span>
            ),
        },
        {
            key: "quality", label: "JD quality", width: 140, sortable: true, filterable: false,
            accessor: (j) => quality[j.id]?.score ?? -1,
            render: (j) => <JobQualityBadge q={quality[j.id]} />,
        },
        {
            key: "skill_health", label: "Skills", width: 120, sortable: true, filterable: false,
            accessor: (j) => j.skill_health?.score ?? -1,
            render: (j) => <SkillHealthBadge health={j.skill_health} testId={`job-skill-health-${j.id}`} />,
        },
        {
            key: "team", label: "Recruiters", width: 160, sortable: true, filterable: false,
            accessor: (j) => {
                const lead = (insights[j.id]?.assignments || []).find((a) => a.role === "lead");
                return lead?.name || "";
            },
            render: (j) => {
                const as = insights[j.id]?.assignments || [];
                if (!as.length) return <span className="text-foreground/30">—</span>;
                const lead = as.find((a) => a.role === "lead");
                const others = as.filter((a) => a.role !== "lead");
                return (
                    <span className="inline-flex items-center gap-1 text-xs truncate" title={as.map((a) => `${a.role === "lead" ? "★ " : ""}${a.name}`).join(", ")} data-testid={`job-team-${j.id}`}>
                        {lead && <Star className="w-3 h-3 fill-amber-500 text-amber-500 shrink-0" />}
                        <span className="truncate">{(lead || others[0])?.name}</span>
                        {as.length > 1 && <span className="text-foreground/50 shrink-0">+{as.length - 1}</span>}
                    </span>
                );
            },
        },
        {
            key: "health", label: "Health", width: 90, sortable: true, filterable: false,
            accessor: (j) => insights[j.id]?.health?.score ?? -1,
            render: (j) => {
                const h = insights[j.id]?.health;
                if (!h) return <span className="text-foreground/30">—</span>;
                const comp = h.components || {};
                const tip = `Velocity ${comp.velocity ?? "n/a"} · Aging ${comp.aging ?? "n/a"} · Activity ${comp.activity ?? "n/a"} · Target ${comp.target ?? "n/a"} · SLA ${comp.sla ?? "n/a"}`;
                return (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${healthCls(h.score)}`} title={tip} data-testid={`job-list-health-${j.id}`}>
                        <Activity className="w-3 h-3" /> {h.score}
                    </span>
                );
            },
        },
        {
            key: "sla", label: "SLA", width: 100, sortable: true, filterable: false,
            accessor: (j) => insights[j.id]?.sla_ratio ?? -1,
            render: (j) => {
                const ins = insights[j.id];
                if (!ins?.sla_status) return <span className="text-foreground/30">—</span>;
                const label = ins.sla_status === "breach"
                    ? `+${ins.days_open - ins.sla_days}d over`
                    : `${Math.max(0, ins.sla_days - ins.days_open)}d left`;
                return (
                    <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${SLA_CLS[ins.sla_status]}`} title={`${ins.days_open}d open of ${ins.sla_days}d SLA`} data-testid={`job-list-sla-${j.id}`}>
                        {label}
                    </span>
                );
            },
        },
        {
            key: "days_open", label: "Open", width: 70, sortable: true, filterable: false, align: "right",
            accessor: (j) => insights[j.id]?.days_open ?? -1,
            render: (j) => {
                const d = insights[j.id]?.days_open;
                return d !== undefined ? <span className="font-mono text-xs">{d}d</span> : <span className="text-foreground/30">—</span>;
            },
        },
        {
            key: "openings", label: "Openings", width: 90, sortable: true, editable: true, align: "right",
            render: (j, v) => <span className="font-mono text-xs">{v ?? 1}</span>,
        },
        {
            key: "priority", label: "Priority", width: 90, sortable: true, editable: true,
            // Rank so urgent → high → normal → low sorts naturally (desc = highest first).
            accessor: (j) => PRIORITY_RANK[(j.priority || "normal").toLowerCase()] || 0,
            editor: (props) => <SelectInlineEditor {...props} options={PRIORITY_OPTIONS} />,
            render: (j) => {
                const p = (j.priority || "normal").toLowerCase();
                return <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${PRIORITY_STYLE[p] || ""}`}>{p}</span>;
            },
        },
        {
            // B14.6 — Promoted / highlighted jobs. When a job is flagged, it
            // gets an amber left-border on the row (see rowClassName below)
            // AND appears at the top of embed boards (see routers/embed.py).
            // Click the star to toggle.
            key: "highlight", label: "Promo", width: 70, sortable: true,
            accessor: (j) => j.highlight ? 1 : 0,
            noRowClick: true,
            render: (j) => (
                <SpotlightPickerTrigger
                    job={j}
                    onSaved={(payload) => {
                        setJobs((cur) => cur.map((x) => x.id === j.id
                            ? { ...x, highlight: payload.highlight, highlight_color: payload.highlight_color, spotlight_until: payload.spotlight_until }
                            : x));
                    }}
                />
            ),
        },
        {
            key: "urgency", label: "Urgency", width: 100, sortable: true, editable: true,
            editor: (props) => <SelectInlineEditor {...props} options={URGENCY_OPTIONS} />,
        },
        { key: "location", label: "Location", width: 140, sortable: true, editable: true },
        {
            key: "work_mode", label: "Work mode", width: 110, sortable: true, editable: true,
            editor: (props) => <SelectInlineEditor {...props} options={[["On-site", "On-site"], ["Hybrid", "Hybrid"], ["Remote", "Remote"]]} />,
        },
        {
            key: "employment_type", label: "Type", width: 110, sortable: true, editable: true,
            editor: (props) => <SelectInlineEditor {...props} options={[["Full-time", "Full-time"], ["Part-time", "Part-time"], ["Contract", "Contract"], ["Internship", "Internship"], ["Freelance", "Freelance"]]} />,
        },
        {
            key: "experience", label: "YoE", width: 80, sortable: true,
            align: "right",
            accessor: (j) => j.experience_min ?? 0,
            render: (j) => <span className="font-mono text-xs">{j.experience_min ?? 0}–{j.experience_max ?? 0}</span>,
        },
        {
            key: "salary_min", label: "Salary min", width: 110, sortable: true, editable: true, align: "right",
            render: (j, v) => v ? <span className="font-mono text-xs">{(j.salary_currency || "INR")} {Number(v).toLocaleString()}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "salary_max", label: "Salary max", width: 110, sortable: true, editable: true, align: "right",
            render: (j, v) => v ? <span className="font-mono text-xs">{(j.salary_currency || "INR")} {Number(v).toLocaleString()}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "applicant_count", label: "Apps", width: 70, sortable: true, align: "right",
            render: (j) => (
                <span className="inline-flex items-center gap-1 font-mono text-xs">
                    <Users className="w-3 h-3 text-foreground/60" />{j.applicant_count || 0}
                </span>
            ),
        },
        {
            key: "due_date", label: "Due", width: 90, sortable: true, editable: true,
            editor: ({ value, onChange, onCommit, onCancel }) => (
                <input
                    type="date" autoFocus
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onCommit}
                    onKeyDown={(e) => { if (e.key === "Enter") onCommit(); if (e.key === "Escape") onCancel(); }}
                    className="w-full h-9 px-1 text-xs border-2 border-ikb bg-white focus:outline-none"
                />
            ),
            render: (j, v) => fmtDate(v) ? <span className="font-mono text-xs">{fmtDate(v)}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "created_at", label: "Created", width: 90, sortable: true, filterable: false,
            render: (j, v) => <span className="font-mono text-xs">{fmtDate(v) || "—"}</span>,
        },
        {
            key: "created_by_name", label: "Created by", width: 130, sortable: true,
            render: (j, v) => v ? <span className="text-xs truncate">{v}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "updated_at", label: "Updated", width: 90, sortable: true, filterable: false,
            render: (j, v) => <span className="font-mono text-xs">{fmtDate(v) || "—"}</span>,
        },
    ], [quality, insights, selected]);

    const cloneJob = async (job) => {
        try {
            const { data } = await api.post(`/jobs/${job.id}/clone`);
            toast.success(`Cloned to "${data.title}" — opening editor`);
            navigate(`/jobs/${data.id}/edit`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const renderActions = (j) => (
        <>
            <Link to={`/jobs/${j.id}`} className="p-1 border border-border hover:bg-secondary inline-flex" title="Open" data-testid={`job-action-open-${j.id}`}>
                <Users className="w-3 h-3" />
            </Link>
            <Link to={`/jobs/${j.id}/edit`} className="p-1 border border-border hover:bg-secondary inline-flex" title="Edit" data-testid={`job-action-edit-${j.id}`}>
                <Pencil className="w-3 h-3" />
            </Link>
            <button
                type="button"
                onClick={() => cloneJob(j)}
                className="p-1 border border-border hover:bg-blue-50 hover:border-ikb hover:text-ikb inline-flex"
                title="Clone this job as a new draft"
                data-testid={`job-action-clone-${j.id}`}
            >
                <Copy className="w-3 h-3" />
            </button>
            {j.public_url && (
                <a href={j.public_url} target="_blank" rel="noreferrer" className="p-1 border border-border hover:bg-secondary inline-flex" title="Public page" data-testid={`job-action-public-${j.id}`}>
                    <ExternalLink className="w-3 h-3" />
                </a>
            )}
        </>
    );

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4 sm:space-y-5">
            <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Jobs</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">All job openings</h1>
                    <p className="hidden sm:block text-xs text-muted-foreground font-mono mt-1">Double-click any cell to edit · Press Esc to cancel · Click column headers to sort</p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        className="rounded-none"
                        data-testid="jobs-refresh-btn"
                        title="Re-fetch the jobs list (the page also caches for 30s so back-navigation is instant)"
                    >
                        {loading ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 sm:mr-2" />}
                        <span className="hidden sm:inline">Refresh</span>
                    </Button>
                    <Link to="/jobs/bulk-create" className="flex-1 sm:flex-none"><Button variant="outline" className="rounded-none w-full sm:w-auto" data-testid="jobs-bulk-btn">
                        <Wand2 className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Bulk import</span><span className="sm:hidden ml-1">Bulk</span>
                    </Button></Link>
                    <Link to="/jobs/new" className="flex-1 sm:flex-none"><Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white w-full sm:w-auto" data-testid="jobs-new-btn"><Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">New job</span><span className="sm:hidden ml-1">New</span></Button></Link>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-border -mx-1 overflow-x-auto">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.key || "all"}
                        type="button"
                        onClick={() => setStatus(t.key)}
                        data-testid={`jobs-tab-${t.key || "all"}`}
                        className={`shrink-0 px-3 py-2 -mb-px text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${
                            status === t.key
                                ? "border-ikb text-ikb"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {t.label} · {counts[t.key] || 0}
                    </button>
                ))}
                {/* R21 — JD Health filter (skill_health trend-based) */}
                <div className="ml-auto flex items-center gap-1 shrink-0 pl-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mr-1">JD health</span>
                    {[
                        { key: "", label: "All", testid: "jobs-health-all" },
                        { key: "healthy", label: "Healthy 75+", testid: "jobs-health-healthy" },
                        { key: "risky", label: "At risk", testid: "jobs-health-risky" },
                        { key: "obsolete", label: "Obsolete tags", testid: "jobs-health-obsolete" },
                    ].map((h) => (
                        <button
                            key={h.testid}
                            type="button"
                            onClick={() => setHealthFilter(h.key)}
                            data-testid={h.testid}
                            className={`h-7 px-2 text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                                healthFilter === h.key
                                    ? "border-ikb bg-ikb text-white"
                                    : "border-border hover:border-ikb hover:text-ikb"
                            }`}
                        >
                            {h.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* B7 — richer filter row: priority, work_mode, urgency, department, industry, search */}
            <div className="flex flex-wrap items-center gap-2 py-2 border-b border-border" data-testid="jobs-filter-row">
                <div className="relative">
                    <input
                        type="text"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Search title, slug, skills…"
                        className="h-8 pl-2 pr-2 border border-border bg-white text-sm w-56"
                        data-testid="jobs-filter-search"
                    />
                </div>
                <FilterSelect label="Priority" value={priorityFilter} onChange={setPriorityFilter}
                    options={[["", "Any priority"], ["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]]}
                    testId="jobs-filter-priority" />
                <FilterSelect label="Work mode" value={workModeFilter} onChange={setWorkModeFilter}
                    options={[["", "Any mode"], ["remote", "Remote"], ["hybrid", "Hybrid"], ["onsite", "Onsite"]]}
                    testId="jobs-filter-work-mode" />
                <FilterSelect label="Urgency" value={urgencyFilter} onChange={setUrgencyFilter}
                    options={[["", "Any urgency"], ["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]]}
                    testId="jobs-filter-urgency" />
                {filterOptions.departments.length > 0 && (
                    <FilterSelect label="Department" value={departmentFilter} onChange={setDepartmentFilter}
                        options={[["", "All departments"], ...filterOptions.departments.map((d) => [d, d])]}
                        testId="jobs-filter-department" />
                )}
                {filterOptions.industries.length > 0 && (
                    <FilterSelect label="Industry" value={industryFilter} onChange={setIndustryFilter}
                        options={[["", "All industries"], ...filterOptions.industries.map((i) => [i, i])]}
                        testId="jobs-filter-industry" />
                )}
                {(priorityFilter || workModeFilter || urgencyFilter || departmentFilter || industryFilter || searchQ) && (
                    <button
                        type="button"
                        onClick={() => {
                            setPriorityFilter(""); setWorkModeFilter(""); setUrgencyFilter("");
                            setDepartmentFilter(""); setIndustryFilter(""); setSearchQ("");
                        }}
                        className="h-8 px-2 text-[10px] font-mono uppercase tracking-widest border border-border hover:border-ikb hover:text-ikb inline-flex items-center gap-1"
                        data-testid="jobs-filter-clear"
                    >
                        Clear filters
                    </button>
                )}
                <span className="ml-auto text-[11px] font-mono text-muted-foreground" data-testid="jobs-filter-count">
                    {filteredJobs.length} of {jobs.length}
                </span>
            </div>

            {selected.size > 0 && (
                <div className="flex items-center gap-3 border-2 border-ikb bg-blue-50/60 px-3 py-2" data-testid="jobs-bulk-bar">
                    <span className="text-[11px] font-mono uppercase tracking-widest font-bold text-ikb">{selected.size} job{selected.size !== 1 ? "s" : ""} selected</span>
                    <button
                        type="button"
                        onClick={() => setAssignOpen(true)}
                        className="h-8 px-3 text-[11px] font-mono uppercase tracking-widest bg-ikb text-white hover:bg-[#001f7a] inline-flex items-center gap-1.5"
                        data-testid="jobs-bulk-assign-btn"
                    >
                        <UserPlus className="w-3.5 h-3.5" /> Assign recruiters
                    </button>
                    <button
                        type="button"
                        onClick={() => setBulkEditOpen(true)}
                        className="h-8 px-3 text-[11px] font-mono uppercase tracking-widest border border-ikb text-ikb hover:bg-ikb hover:text-white inline-flex items-center gap-1.5"
                        data-testid="jobs-bulk-edit-btn"
                        title="Bulk edit status, priority, due date, work mode, urgency, openings"
                    >
                        Bulk edit
                    </button>
                    <button
                        type="button"
                        onClick={() => openBulkForSelectedJobs("interview")}
                        disabled={bulkCandLoading}
                        className="h-8 px-3 text-[11px] font-mono uppercase tracking-widest border border-ikb/60 text-ikb hover:bg-ikb/10 inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="jobs-bulk-interview-btn"
                        title="Create interview rounds for all candidates in the selected jobs"
                    >
                        {bulkCandLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BriefcaseIcon className="w-3.5 h-3.5" />} Interview
                    </button>
                    <button
                        type="button"
                        onClick={() => openBulkForSelectedJobs("skills")}
                        disabled={bulkCandLoading}
                        className="h-8 px-3 text-[11px] font-mono uppercase tracking-widest border border-emerald-500 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="jobs-bulk-skills-btn"
                        title="Push rating skills to upcoming + future interviews for all candidates in the selected jobs"
                    >
                        {bulkCandLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Skills
                    </button>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set(filteredJobs.map((j) => j.id)))}
                        className="h-8 px-2 text-[11px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb"
                        data-testid="jobs-bulk-select-all"
                    >
                        Select all ({filteredJobs.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="ml-auto h-8 px-2 text-[11px] font-mono uppercase tracking-widest text-foreground/60 hover:text-foreground"
                        data-testid="jobs-bulk-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            <DataTable
                columns={columns}
                rows={filteredJobs}
                freezeFirstColumn={2}
                rowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/jobs/${r.id}`)}
                onCellEdit={onCellEdit}
                actions={renderActions}
                storageKey="jobs"
                testId="jobs-table"
                loading={loading}
                emptyMessage="No jobs found. Adjust filters or create one."
                rowClassName={(r) => r.highlight ? "border-l-4 border-l-amber-400 bg-amber-50/40" : ""}
            />

            {assignOpen && (
                <BulkAssignRecruitersDialog
                    jobIds={[...selected]}
                    recruiters={recruiters}
                    onClose={() => setAssignOpen(false)}
                    onSaved={() => { setSelected(new Set()); loadInsights(); }}
                />
            )}
            {bulkEditOpen && (
                <BulkJobEditDialog
                    jobIds={[...selected]}
                    onClose={() => setBulkEditOpen(false)}
                    onSaved={() => {
                        setBulkEditOpen(false);
                        setSelected(new Set());
                        // Full reload — bulk-edit can change status/priority/date fields
                        // that all show as columns in the table.
                        load();
                        loadInsights();
                    }}
                />
            )}
            <BulkInterviewAssignDialog
                open={bulkInterviewOpen}
                candidateIds={bulkCandidateIds}
                onClose={() => setBulkInterviewOpen(false)}
                onDone={() => { setBulkInterviewOpen(false); setSelected(new Set()); setBulkCandidateIds([]); loadInsights(); }}
            />
            <BulkPushSkillsDialog
                open={bulkSkillsOpen}
                candidateIds={bulkCandidateIds}
                onClose={() => setBulkSkillsOpen(false)}
                onDone={() => { setBulkSkillsOpen(false); setSelected(new Set()); setBulkCandidateIds([]); }}
            />
        </div>
    );
}

/**
 * FilterSelect — compact labelled dropdown for the Jobs list filter row.
 * The visible label sits inline before the select so the row stays a single
 * horizontal strip with no external <Label> element.
 */
function FilterSelect({ label, value, onChange, options, testId }) {
    return (
        <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
            {label}:
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 border border-border bg-white text-xs px-1.5 font-mono uppercase tracking-widest"
                data-testid={testId}
            >
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}

